import { twMerge } from '@/lib/classname/twMerge'
import type { IconSize } from '@/ui/icons/icon/Icon'
import { Icon } from '@/ui/icons/icon/Icon'
import { type VariantProps, cva } from 'class-variance-authority'
import { cloneElement } from 'react'

const ICON_SIZE: IconSize = 'md'

export const infoCardVariants = {
  info: 'border-info-fg bg-info-bg text-info-fg',
  success: 'border-success-fg bg-success-bg text-success-fg',
  error: 'border-error-fg bg-error-bg text-error-fg',
  warning: 'border-warning-fg bg-warning-bg text-warning-fg',
} as const

const infoCardCva = cva(
  'flex items-center gap-2 rounded-sm border-px border-solid p-2 hd:border-1.5',
  {
    variants: {
      variant: infoCardVariants,
    },
  },
)

const infoCardIconCva = cva('shrink-0', {
  variants: {
    variant: {
      info: 'text-info-fg',
      success: 'text-success-fg',
      error: 'text-error-fg',
      warning: 'text-warning-fg',
    },
  },
})

export type InfoCardVariant = keyof typeof infoCardVariants

type InfoCardCvaProps = VariantProps<typeof infoCardCva>

/** `variant` is required and non-null (CVA’s `VariantProps` still allows `null` on variant keys). */
export type InfoCardVariantProps = RequiredProps<InfoCardCvaProps, 'variant'> & {
  variant: NonNullable<InfoCardCvaProps['variant']>
}

/** Props that `Icon.*` components accept; we only merge `size` + `className`. */
export type InfoCardIconSlotProps = {
  size?: IconSize
  className?: string
}

export type InfoCardProps = UiComponentProps &
  InfoCardVariantProps & {
    /** Bold heading. Omit when the card only has a body (`content`). */
    title?: React.ReactNode
    /** Body copy under the title. Omit for title-only banners. */
    content?: React.ReactNode
    /** Optional icon override (`Icon.*`). If omitted, a default icon for `variant` is used. */
    icon?: React.ReactElement<InfoCardIconSlotProps>
    /** Optional row below `content` (e.g. link with icons). */
    action?: React.ReactNode
  }

const defaultIconByVariant: Record<InfoCardVariant, React.ReactElement<InfoCardIconSlotProps>> = {
  info: <Icon.Info />,
  success: <Icon.CircleCheck />,
  error: <Icon.CircleAlert />,
  warning: <Icon.TriangleAlert />,
}

function iconForVariant(
  variant: InfoCardVariant,
  override: React.ReactElement<InfoCardIconSlotProps> | undefined,
): React.ReactElement {
  const icon = override ?? defaultIconByVariant[variant]

  return cloneElement(icon, {
    size: ICON_SIZE,
    className: twMerge(infoCardIconCva({ variant }), icon.props.className),
  })
}

export function InfoCard({
  variant,
  title,
  content,
  icon,
  action,
  id,
  className,
  'data-testid': dataTestId,
  'aria-label': ariaLabel,
}: InfoCardProps) {
  return (
    <div
      id={id}
      className={twMerge(className, infoCardCva({ variant }))}
      aria-label={ariaLabel}
      data-testid={dataTestId ?? 'info-card'}
    >
      <span
        aria-hidden="true"
        className={twMerge(
          'flex shrink-0 items-center justify-center',
          infoCardIconCva({ variant }),
        )}
        data-testid="info-card/icon"
      >
        {iconForVariant(variant, icon)}
      </span>
      <div className="flex min-w-0 grow flex-col gap-0">
        {title && <p className="font-bold leading-light">{title}</p>}
        {content && <div className="font-medium leading-light text-brand-2-900">{content}</div>}
        {action && (
          <div className="font-bold text-brand-2-900" data-testid="info-card/action">
            {action}
          </div>
        )}
      </div>
    </div>
  )
}
